import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Check, ChevronDown, Search, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function TenantSwitcher() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/auth/memberships").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const memberships = data?.memberships || [];
  const filtered = useMemo(() => {
    if (!query.trim()) return memberships;
    const q = query.trim().toLowerCase();
    // Simple contains-any-word match — good enough for a few hundred tenant
    // names, no fuzzy-match library needed. Case-insensitive on name, role,
    // and tenant_id (so an admin can paste a UUID and find it fast).
    return memberships.filter((m) => (
      (m.tenant_name || "").toLowerCase().includes(q) ||
      (m.tenant_id || "").toLowerCase().includes(q) ||
      (m.role || "").toLowerCase().includes(q)
    ));
  }, [memberships, query]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQuery("");
  }, [open]);

  if (memberships.length < 2) return null;
  const active = memberships.find((m) => m.tenant_id === data.active_tenant_id);

  const switchTo = async (tid) => {
    if (tid === data.active_tenant_id || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/switch-tenant", { tenant_id: tid });
      toast.success("Switched workspace");
      window.location.href = "/";
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to switch"); setBusy(false); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="tenant-switcher"
                className="flex items-center gap-1.5 h-9 px-3 rounded-xl text-xs font-medium text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] transition-colors border border-[color:var(--glass-border)]">
          <Building2 className="w-3.5 h-3.5" />
          <span className="max-w-[130px] truncate">{active?.tenant_name || data.active_tenant_id}</span>
          <ChevronDown className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <div className="px-3 py-2 flex items-center justify-between">
          <DropdownMenuLabel className="text-[11px] p-0">{data.staff ? "Work inside a tenant" : "Switch tenant"}</DropdownMenuLabel>
          <span className="text-[10px] text-[color:var(--ink-3)]">{filtered.length}/{memberships.length}</span>
        </div>
        {data.staff && (
          <p data-testid="tenant-staff-note" className="px-3 pb-2 text-[10px] text-[color:var(--ink-3)] leading-snug">
            Everything you do inside a tenant is logged there as Eazy staff activity.
          </p>
        )}
        {memberships.length >= 5 && (
          <div className="px-2 pb-1">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[color:var(--ink-3)] absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={inputRef}
                data-testid="tenant-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tenants…"
                onKeyDown={(e) => {
                  // Enter picks the first match when there's exactly one/many.
                  if (e.key === "Enter" && filtered.length > 0) {
                    e.preventDefault();
                    switchTo(filtered[0].tenant_id);
                    setOpen(false);
                  }
                }}
                className="w-full h-8 pl-7 pr-7 rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--glass-border)] text-xs text-[color:var(--ink)] placeholder:text-[color:var(--ink-3)] focus:outline-none focus:ring-1 focus:ring-[color:var(--brand-ocean)]"
              />
              {query && (
                <button data-testid="tenant-search-clear" onClick={() => setQuery("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        )}
        <DropdownMenuSeparator />
        <div className="max-h-[280px] overflow-y-auto" data-testid="tenant-list">
          {filtered.length === 0 ? (
            <div data-testid="tenant-empty" className="px-3 py-6 text-center text-xs text-[color:var(--ink-3)]">
              No tenants match “{query}”
            </div>
          ) : filtered.map((m) => (
            <DropdownMenuItem key={m.tenant_id}
                              data-testid={`tenant-option-${m.tenant_id}`}
                              onClick={() => switchTo(m.tenant_id)}
                              className="text-xs">
              <span className="flex-1 truncate">{m.tenant_name}</span>
              <span className="text-[9px] text-[color:var(--ink-3)] uppercase mr-1">{m.role}</span>
              {m.tenant_id === data.active_tenant_id && <Check className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
